import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { Building2, Server, AlertTriangle, Check } from 'lucide-react';
import { RouteParams } from '@/router/type';
import { useDemoOrganizationStore } from '@/store/demo-organization';
import { useDemoWorkspaceStore } from '@/store/demo-workspace';
import { Alert, AlertDescription } from '@/components/ui/alert';

type GPUType = 'h200' | 'a100' | 'l4';

const GPU_LABELS: Record<GPUType, string> = {
  h200: 'H200 141GB',
  a100: 'A100 80GB',
  l4: 'L4 24GB',
};

const DemoOrgQuotaSettings = () => {
  const { orgId } = useParams<RouteParams>();
  const organizations = useDemoOrganizationStore((state) => state.organizations);
  const updateOrganizationQuota = useDemoOrganizationStore((state) => state.updateOrganizationQuota);
  const workspaces = useDemoWorkspaceStore((state) => state.workspaces);
  const updateWorkspaceQuota = useDemoWorkspaceStore((state) => state.updateQuota);

  const organization = organizations.find((org) => org.id === orgId);
  const orgWorkspaces = workspaces.filter((ws) => ws.organizationId === orgId);

  const [editingOrgQuota, setEditingOrgQuota] = useState(false);
  const [orgQuotaValues, setOrgQuotaValues] = useState({
    h200: organization?.quota.h200.total || 0,
    a100: organization?.quota.a100.total || 0,
    l4: organization?.quota.l4.total || 0,
  });

  const [workspaceQuotas, setWorkspaceQuotas] = useState<Record<string, Record<GPUType, number>>>(() => {
    const initial: Record<string, Record<GPUType, number>> = {};
    orgWorkspaces.forEach((ws) => {
      initial[ws.id] = {
        h200: ws.quota.h200.total,
        a100: ws.quota.a100.total,
        l4: ws.quota.l4.total,
      };
    });
    return initial;
  });

  if (!organization) {
    return (
      <div className="container py-8">
        <p className="text-muted-foreground">Organization not found</p>
      </div>
    );
  }

  // Calculate allocated quota (sum of all workspace quotas)
  const getAllocatedQuota = (gpuType: GPUType) => {
    return orgWorkspaces.reduce((sum, ws) => {
      const wsQuota = workspaceQuotas[ws.id]?.[gpuType] ?? ws.quota[gpuType].total;
      return sum + wsQuota;
    }, 0);
  };

  // Calculate available quota (org total - allocated)
  const getAvailableQuota = (gpuType: GPUType) => {
    const orgTotal = editingOrgQuota ? orgQuotaValues[gpuType] : organization.quota[gpuType].total;
    return orgTotal - getAllocatedQuota(gpuType);
  };

  const handleSaveOrgQuota = () => {
    (['h200', 'a100', 'l4'] as GPUType[]).forEach((gpuType) => {
      updateOrganizationQuota(orgId!, gpuType, {
        total: orgQuotaValues[gpuType],
        used: organization.quota[gpuType].used,
      });
    });
    setEditingOrgQuota(false);
  };

  const handleWorkspaceQuotaChange = (workspaceId: string, gpuType: GPUType, value: number) => {
    setWorkspaceQuotas((prev) => ({
      ...prev,
      [workspaceId]: {
        ...prev[workspaceId],
        [gpuType]: value,
      },
    }));
  };

  const handleSaveWorkspaceQuota = (workspaceId: string) => {
    const ws = orgWorkspaces.find((w) => w.id === workspaceId);
    if (!ws) return;

    // Update each GPU type separately
    (['h200', 'a100', 'l4'] as GPUType[]).forEach((gpuType) => {
      updateWorkspaceQuota(workspaceId, gpuType, {
        total: workspaceQuotas[workspaceId][gpuType],
        used: ws.quota[gpuType].used,
      });
    });
  };

  const hasOverallocation = (gpuType: GPUType) => getAvailableQuota(gpuType) < 0;
  const hasAnyOverallocation = (['h200', 'a100', 'l4'] as GPUType[]).some(hasOverallocation);

  return (
    <div className="container py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">GPU Quota Management</h1>
        <p className="text-muted-foreground">
          Manage organization GPU quota and assign to workspaces
        </p>
      </div>

      {hasAnyOverallocation && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Warning: Some GPU types have more quota assigned to workspaces than available in the organization.
          </AlertDescription>
        </Alert>
      )}

      {/* Organization Total Quota */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-orange-500" />
                Organization GPU Quota
              </CardTitle>
              <CardDescription>
                Total GPU resources available to this organization
              </CardDescription>
            </div>
            {!editingOrgQuota ? (
              <Button variant="outline" onClick={() => setEditingOrgQuota(true)}>
                Edit Quota
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditingOrgQuota(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveOrgQuota}>
                  <Check className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            {(['h200', 'a100', 'l4'] as GPUType[]).map((gpuType) => {
              const orgTotal = editingOrgQuota ? orgQuotaValues[gpuType] : organization.quota[gpuType].total;
              const allocated = getAllocatedQuota(gpuType);
              const available = orgTotal - allocated;
              const usedPct = orgTotal > 0 ? (allocated / orgTotal) * 100 : 0;

              return (
                <div key={gpuType} className="space-y-3 p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">{GPU_LABELS[gpuType]}</Label>
                    {editingOrgQuota ? (
                      <Input
                        type="number"
                        min={0}
                        value={orgQuotaValues[gpuType]}
                        onChange={(e) =>
                          setOrgQuotaValues((prev) => ({
                            ...prev,
                            [gpuType]: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-20 h-8"
                      />
                    ) : (
                      <span className="text-2xl font-bold">{orgTotal}</span>
                    )}
                  </div>
                  <Progress value={Math.min(usedPct, 100)} className="h-2" />
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Allocated: {allocated}</span>
                    <span className={available < 0 ? 'text-destructive font-medium' : 'text-green-600'}>
                      Available: {available}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Workspace Quota Assignments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-blue-500" />
            Workspace Quota Assignments
          </CardTitle>
          <CardDescription>
            Assign GPU quota from organization pool to each workspace
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {orgWorkspaces.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No workspaces in this organization
            </p>
          ) : (
            orgWorkspaces.map((ws) => (
              <div key={ws.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold">{ws.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {ws.type === 'personal' ? 'Personal workspace' : 'Team workspace'}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleSaveWorkspaceQuota(ws.id)}
                  >
                    Save
                  </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  {(['h200', 'a100', 'l4'] as GPUType[]).map((gpuType) => {
                    const currentValue = workspaceQuotas[ws.id]?.[gpuType] ?? ws.quota[gpuType].total;
                    const maxAvailable = (editingOrgQuota ? orgQuotaValues[gpuType] : organization.quota[gpuType].total);
                    const usedByWs = ws.quota[gpuType].used;

                    return (
                      <div key={gpuType} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">{GPU_LABELS[gpuType]}</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={usedByWs}
                              max={maxAvailable}
                              value={currentValue}
                              onChange={(e) =>
                                handleWorkspaceQuotaChange(
                                  ws.id,
                                  gpuType,
                                  parseInt(e.target.value) || 0
                                )
                              }
                              className="w-16 h-7 text-sm"
                            />
                          </div>
                        </div>
                        <Slider
                          value={[currentValue]}
                          min={0}
                          max={maxAvailable}
                          step={1}
                          onValueChange={([value]) =>
                            handleWorkspaceQuotaChange(ws.id, gpuType, value)
                          }
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Used: {usedByWs}</span>
                          <span>Assigned: {currentValue}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Quota Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium">Resource</th>
                  <th className="text-right py-2 font-medium">Org Total</th>
                  <th className="text-right py-2 font-medium">Allocated</th>
                  <th className="text-right py-2 font-medium">Available</th>
                  <th className="text-right py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {(['h200', 'a100', 'l4'] as GPUType[]).map((gpuType) => {
                  const orgTotal = editingOrgQuota ? orgQuotaValues[gpuType] : organization.quota[gpuType].total;
                  const allocated = getAllocatedQuota(gpuType);
                  const available = orgTotal - allocated;
                  const isOver = available < 0;

                  return (
                    <tr key={gpuType} className="border-b last:border-0">
                      <td className="py-2 font-medium">{GPU_LABELS[gpuType]}</td>
                      <td className="py-2 text-right">{orgTotal}</td>
                      <td className="py-2 text-right">{allocated}</td>
                      <td className={`py-2 text-right ${isOver ? 'text-destructive' : 'text-green-600'}`}>
                        {available}
                      </td>
                      <td className="py-2 text-right">
                        {isOver ? (
                          <Badge variant="destructive">Over-allocated</Badge>
                        ) : available === 0 ? (
                          <Badge variant="secondary">Fully Allocated</Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-600 border-green-600">Available</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoOrgQuotaSettings;
